import { useLayoutEffect } from "react";
import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import Wrapper from "../../helpers/Wrapper";

am5.addLicense("AM5C258896422");

const CHART_STYLES = {
  MAT_2Y: {
    color: "#6c5ce7",
    strokeDasharray: [5, 5]
  },
  MAT_TY: {
    color: "#007bff",
    strokeDasharray: [5, 5]
  },
  MAT_LY: {
    color: "#ff9500",
    strokeDasharray: [3, 3]
  }
};

const VerticalLineChart = ({
  chartId,
  chartClass,
  chartData,
  chartLabels
}) => {
  useLayoutEffect(() => {
    let root = am5.Root.new(chartId);
    let chart;

    // Function to format series data
    const formatData = (dataValues, labels) => {
      return labels.map((label, index) => ({
        month: label,
        value: dataValues[index]
      }));
    };

    try {
      // Set themes
      root.setThemes([am5themes_Animated.new(root)]);

      // Create chart
      chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          focusable: true,
          panX: true,
          panY: false,
          wheelX: "none",
          wheelY: "none",
          paddingLeft: 0,
          paddingTop: 20,
          paddingBottom: 50
        })
      );

      // Create X-Axis (Category Axis)
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          renderer: am5xy.AxisRendererX.new(root, {
            minGridDistance: 30
          }),
          categoryField: "month",
          tooltip: am5.Tooltip.new(root, {})
        })
      );

      // Configure X-axis labels
      xAxis.get("renderer").labels.template.setAll({
        fontSize: 11, // Set font size to 11px
        maxWidth: 80, // Maximum width before truncation
        oversizedBehavior: "truncate", // Use "truncate" for ellipsis
        textAlign: "center",
        ellipsis: "...", // Specify ellipsis characters
        rotation: 0, // Optional: if you want to rotate labels
        paddingTop: 8,
        paddingBottom: 8
      });

      // Set X-axis data from chartLabels
      xAxis.data.setAll(chartLabels.map(label => ({ month: label })));

      // Create Y-Axis (Value Axis)
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {})
        })
      );

      // Hide grid lines
      xAxis.get("renderer").grid.template.set("visible", false);
      yAxis.get("renderer").grid.template.set("visible", false);

      // Add cursor
      const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
        behavior: "zoomX"
      }));
      cursor.lineY.set("visible", false);

      // Create legend
      const legend = chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.p50,
          x: am5.p50,
          y: am5.p100
        })
      );

      // Modified createSeries function
      const createSeries = (name, data) => {
        const style = CHART_STYLES[name.replace(/\s/g, '_')] || {
          color: "#007bff",
          strokeDasharray: []
        };

        const series = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: name,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: "value",
            categoryXField: "month",
            stroke: am5.color(style.color),
            fill: am5.color(style.color)
          })
        );

        series.strokes.template.setAll({
          strokeDasharray: style.strokeDasharray,
          strokeWidth: 2
        });

        // Add bullet points
        series.bullets.push(() => am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 5,
            fill: root.interfaceColors.get("background"),
            stroke: am5.color(style.color),
            strokeWidth: 2
          })
        }));

        // Enable tooltip
        series.set("tooltip", am5.Tooltip.new(root, {
          labelText: "{name}\n{categoryX}: {valueY}"
        }));

        series.data.setAll(data);
        return series;
      };

      // Create series for each chartData entry
      chartData.forEach(({ name, data }) => {
        const formattedData = formatData(data, chartLabels);
        createSeries(name, formattedData);
      });

      // Update legend with series data
      legend.data.setAll(chart.series.values);

      // Add Y-axis ranges
      const rangeColors = [0xff0000, 0xffeb3b, 0x00c853];
      const minValue = 0;
      const maxValue = Math.max(...chartData.flatMap(series => series.data)) * 1.1;
      const rangeCount = 3;
      const rangeSize = (maxValue - minValue) / rangeCount;

      for (let i = 0; i < rangeCount; i++) {
        const range = yAxis.makeDataItem({
          value: minValue + i * rangeSize,
          endValue: minValue + (i + 1) * rangeSize
        });
        yAxis.createAxisRange(range);
        range.get("axisFill").setAll({
          fill: am5.color(rangeColors[i]),
          fillOpacity: 0.4,
          visible: true
        });
      }

      chart.appear(1000, 100);

    } catch (error) {
      console.error("Error initializing chart:", error);
    }

    return () => {
      root.dispose();
    };

  }, [chartId, chartData, chartLabels]);

  return (
    <Wrapper>
      <div
        id={chartId}
        className={chartClass}
        style={{ height: "100%" }}
      />
    </Wrapper>
  );
};

VerticalLineChart.propTypes = {
  chartId: PropTypes.string.isRequired,
  chartClass: PropTypes.string,
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(PropTypes.number).isRequired
    })
  ).isRequired,
  chartLabels: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default VerticalLineChart;